import { css } from '@emotion/css';
import clsx from 'clsx';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import * as React from 'react';

import { type GrafanaTheme2 } from '@grafana/data';
import { TimeCompareColorMode } from '@grafana/schema';

import { useStyles2 } from '../../themes/ThemeContext';
import { InlineToast } from '../InlineToast/InlineToast';
import { Tooltip } from '../Tooltip/Tooltip';

import { ColorIndicatorPosition, VizTooltipColorIndicator } from './VizTooltipColorIndicator';
import { VizTooltipColorPlacement, type VizTooltipDelta, type VizTooltipItem } from './types';

interface VizTooltipRowProps extends Omit<VizTooltipItem, 'value'> {
  /** The formatted value to display. Widened from `VizTooltipItem.value` to also accept numbers, null, and ReactNode. */
  value: string | number | null | ReactNode;
  /** CSS `justify-content` value for the row layout. Defaults to `'start'`. */
  justify?: string;
  /** Whether this row corresponds to the closest/hovered series — renders the label in bold. */
  isActive?: boolean;
  /** Right margin applied to the value cell, used to leave room for overlay elements such as the close button. */
  marginRight?: string;
  /**
   * Whether the tooltip is currently pinned (locked open by the user).
   * When pinned, label and value become clickable to copy their text to the clipboard.
   * Defaults to `false`.
   */
  isPinned?: boolean;
  /** When true the value cell becomes vertically scrollable up to a fixed max-height. */
  showValueScroll?: boolean;
  /** When true the color indicator is rendered hollow, signalling the field is not shown in the visualization. */
  isHiddenFromViz?: boolean;
}

enum LabelValueTypes {
  label = 'label',
  value = 'value',
}

const SUCCESSFULLY_COPIED_TEXT = 'Copied to clipboard';
const SHOW_SUCCESS_DURATION = 2 * 1000;
const HORIZONTAL_PX_PER_CHAR = 7;
const CAN_COPY = Boolean(navigator.clipboard && window.isSecureContext);

/** @alpha */
export const VizTooltipRow = ({
  label,
  value,
  color,
  colorIndicator,
  colorPlacement = VizTooltipColorPlacement.first,
  justify,
  isActive = false,
  marginRight,
  isPinned = false,
  lineStyle,
  showValueScroll,
  isHiddenFromViz,
  delta,
}: VizTooltipRowProps) => {
  const styles = useStyles2(getStyles, justify, marginRight);

  const deltaDisplay = delta == null ? null : `(${delta.numeric > 0 ? '+' : ''}${delta.text})`;

  // Same as value takes the series color, which is only known here as a prop, so it cannot come
  // from a theme-derived class like the other modes.
  const deltaTakesValueColor = delta?.colorMode === TimeCompareColorMode.SameAsValue;

  const deltaNode =
    delta == null ? null : (
      <span
        className={deltaTakesValueColor ? undefined : getDeltaClass(delta, styles)}
        style={deltaTakesValueColor ? { color } : undefined}
      >{` ${deltaDisplay}`}</span>
    );

  // the delta is a separate node for coloring, but copying the value should keep both values
  const copyText = deltaDisplay == null ? value : `${value} ${deltaDisplay}`;

  const innerValueScrollStyle: CSSProperties = showValueScroll
    ? {
        maxHeight: 55,
        whiteSpace: 'wrap',
        wordBreak: 'break-word',
        overflowY: 'auto',
      }
    : {
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
        lineHeight: 1.2,
      };

  const [showLabelTooltip, setShowLabelTooltip] = useState(false);

  const [copiedText, setCopiedText] = useState<Record<string, string> | null>(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const labelRef = useRef<null | HTMLDivElement>(null);
  const valueRef = useRef<null | HTMLDivElement>(null);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (showCopySuccess) {
      timeoutId = setTimeout(() => {
        setShowCopySuccess(false);
      }, SHOW_SUCCESS_DURATION);
    }

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [showCopySuccess]);

  const copyToClipboard = async (text: string, type: LabelValueTypes) => {
    if (!CAN_COPY) {
      fallbackCopyToClipboard(text, type);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopiedText({ [`${type}`]: text });
      setShowCopySuccess(true);
    } catch (error) {
      setCopiedText(null);
    }
  };

  const fallbackCopyToClipboard = (text: string, type: LabelValueTypes) => {
    // Use a fallback method for browsers/contexts that don't support the Clipboard API.
    const textarea = document.createElement('textarea');
    labelRef.current?.appendChild(textarea);
    textarea.value = text;
    textarea.focus();
    textarea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopiedText({ [`${type}`]: text });
        setShowCopySuccess(true);
      }
    } catch (err) {
      console.error('Unable to copy to clipboard', err);
    }

    textarea.remove();
  };

  const onMouseEnterLabel = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.currentTarget.offsetWidth < event.currentTarget.scrollWidth) {
      setShowLabelTooltip(true);
    }
  };

  const onMouseLeaveLabel = () => setShowLabelTooltip(false);

  // if label is > 50% window width, try to put label/value pairs on new lines
  if (label.length * HORIZONTAL_PX_PER_CHAR > window.innerWidth / 2) {
    label = label.replaceAll('{', '{\n  ').replaceAll('}', '\n}').replaceAll(', ', ',\n  ');
  }

  return (
    <div className={styles.contentWrapper}>
      {color && colorPlacement === VizTooltipColorPlacement.first && (
        <div className={styles.colorWrapper}>
          <VizTooltipColorIndicator
            color={color}
            colorIndicator={colorIndicator}
            lineStyle={lineStyle}
            isHollow={isHiddenFromViz}
          />
        </div>
      )}
      {label && (
        <div className={styles.labelWrapper}>
          {!isPinned ? (
            <div className={clsx(styles.label, isActive ? styles.activeSeries : '')}>{label}</div>
          ) : (
            <>
              <Tooltip content={label} interactive={false} show={showLabelTooltip}>
                <>
                  {showCopySuccess && copiedText?.label && (
                    <InlineToast placement="top" referenceElement={labelRef.current}>
                      {SUCCESSFULLY_COPIED_TEXT}
                    </InlineToast>
                  )}
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                  <div
                    className={clsx(styles.label, isActive ? styles.activeSeries : '', CAN_COPY ? styles.copy : '')}
                    onMouseEnter={onMouseEnterLabel}
                    onMouseLeave={onMouseLeaveLabel}
                    onClick={() => copyToClipboard(label, LabelValueTypes.label)}
                    ref={labelRef}
                  >
                    {label}
                  </div>
                </>
              </Tooltip>
            </>
          )}
        </div>
      )}

      <div className={styles.valueWrapper}>
        {color && colorPlacement === VizTooltipColorPlacement.leading && (
          <VizTooltipColorIndicator
            color={color}
            colorIndicator={colorIndicator}
            position={ColorIndicatorPosition.Leading}
            lineStyle={lineStyle}
          />
        )}

        {!isPinned ? (
          <div className={styles.value} style={innerValueScrollStyle}>
            {value}
            {deltaNode}
          </div>
        ) : (
          <>
            {showCopySuccess && copiedText?.value && (
              <InlineToast placement="top" referenceElement={valueRef.current}>
                {SUCCESSFULLY_COPIED_TEXT}
              </InlineToast>
            )}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div
              className={clsx(styles.value, CAN_COPY ? styles.copy : '')}
              style={innerValueScrollStyle}
              onClick={() => copyToClipboard(copyText ? copyText.toString() : '', LabelValueTypes.value)}
              ref={valueRef}
            >
              {value}
              {deltaNode}
            </div>
          </>
        )}

        {color && colorPlacement === VizTooltipColorPlacement.trailing && (
          <VizTooltipColorIndicator
            color={color}
            colorIndicator={colorIndicator}
            position={ColorIndicatorPosition.Trailing}
            lineStyle={lineStyle}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Standard treats an increase as favorable, inverted flips that. A delta of zero is left in the
 * default text color since it reads as neither an increase nor a decrease. Same as value never
 * reaches here — its color comes from the row's series color instead of the theme.
 */
const getDeltaClass = (delta: VizTooltipDelta, styles: ReturnType<typeof getStyles>) => {
  if (delta.numeric === 0 || Number.isNaN(delta.numeric)) {
    return undefined;
  }

  const isIncrease = delta.numeric > 0;
  const isFavorable = delta.colorMode === TimeCompareColorMode.Inverted ? !isIncrease : isIncrease;

  return isFavorable ? styles.deltaFavorable : styles.deltaUnfavorable;
};

const getStyles = (theme: GrafanaTheme2, justify = 'start', marginRight?: string) => ({
  contentWrapper: css({
    display: 'flex',
    maxWidth: '100%',
    alignItems: 'start',
    justifyContent: justify,
    columnGap: theme.spacing(0.75),
  }),
  label: css({ display: 'inline' }),
  value: css({
    fontWeight: 500,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  }),
  colorWrapper: css({
    alignSelf: 'center',
    flexShrink: 0,
  }),
  labelWrapper: css({
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.colors.text.secondary,
    fontWeight: 400,
  }),
  valueWrapper: css({
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    alignSelf: 'center',
    marginRight,
  }),
  activeSeries: css({
    fontWeight: theme.typography.fontWeightBold,
    color: theme.colors.text.maxContrast,
  }),
  deltaFavorable: css({
    color: theme.colors.success.text,
  }),
  deltaUnfavorable: css({
    color: theme.colors.error.text,
  }),
  copy: css({
    cursor: 'pointer',
  }),
});
